/**
 * Error tracking module for Ingrediable.
 *
 * Provides a thin abstraction over Sentry. To enable, set VITE_SENTRY_DSN in your .env.
 *
 * Usage:
 *   import { errorTracking } from "./lib/error-tracking";
 *   errorTracking.init();
 *   errorTracking.captureException(error);
 */

let sentryModule: any = null;

export const errorTracking = {
  init() {
    const dsn = (import.meta as any).env?.VITE_SENTRY_DSN;
    if (!dsn) {
      console.debug("[ErrorTracking] Sentry DSN not set — error tracking disabled.");
      return;
    }
    import("@sentry/react").then((Sentry) => {
      sentryModule = Sentry;
      Sentry.init({
        dsn,
        integrations: [
          Sentry.browserTracingIntegration(),
          Sentry.replayIntegration(),
        ],
        tracesSampleRate: 0.1,
        replaysSessionSampleRate: 0.05,
        replaysOnErrorSampleRate: 1.0,
        environment: (import.meta as any).env?.MODE || "production",
      });
      console.debug("[ErrorTracking] Sentry initialized.");
    }).catch(() => {
      console.debug("[ErrorTracking] Sentry not installed — run: npm install @sentry/react");
    });
  },

  captureException(error: Error, context?: Record<string, unknown>) {
    if (sentryModule) {
      sentryModule.captureException(error, context ? { extra: context } : undefined);
    }
    console.error("[Error]", error, context);
  },

  captureMessage(message: string, level: "info" | "warning" | "error" = "info") {
    if (sentryModule) {
      sentryModule.captureMessage(message, level);
    }
    console.log(`[${level}]`, message);
  },

  setUser(user: { id: string; email?: string } | null) {
    if (sentryModule) {
      sentryModule.setUser(user);
    }
  },
};
